// Tx Plan Center — the one-sentence answer at the top of the side car.
//
// Front desk opens the drawer with a patient standing there and needs one
// of three answers: does he owe us money, when is he coming back, what did
// we already do. This module turns the drawer payload into the first two
// as plain sentences so nobody has to read a report and do the subtraction
// themselves.
//
// Every number here comes from get_plan_billing_state — the same RPC
// checkout reads — so the sentence can never disagree with what checkout
// charges. Nothing is computed independently.

import { formatMoney } from "./pipelineHelpers";

// ---------------------------------------------------------------------
// Visit phase grouping — shared by the answer sentence and the Treatment
// folder's visit list so "how many visits" is computed in exactly one
// place. Each plan phase is one planned visit (Visit 1, Visit 2, …).
//
// The phase comes from the procedure_visits row ITSELF (pv.phase_number),
// not its parent item: that's how a bridge's prep can land in Visit 1
// while its cementation lands in Visit 2. Falls back to the parent item's
// phase for legacy rows not yet backfilled, then to 99 so orphans sort to
// the bottom instead of corrupting a real phase group.
// ---------------------------------------------------------------------
export function groupVisitPhases({ visits, items }) {
  const procVisits = visits || [];
  const planItems = items || [];

  const itemsById = new Map();
  for (const it of planItems) {
    itemsById.set(it.id, {
      phase_number: Number(it.phase_number || 1),
      display_label: it.display_label || it.procedure_name || null,
      procedure_name: it.procedure_name || null,
      tooth_number: it.tooth_number || null,
      surface: it.surface || null,
      status: it.status || null,
    });
  }

  const decorated = procVisits.map((v) => {
    const meta = itemsById.get(v.item_id) || {};
    const phase = v.phase_number ?? meta.phase_number ?? 99;
    return {
      ...v,
      display_label: v.display_label || meta.display_label,
      procedure_name: v.procedure_name || meta.procedure_name,
      tooth_number: v.tooth_number || meta.tooth_number,
      surface: v.surface || meta.surface,
      phase_number: Number(phase),
    };
  });

  const phaseMap = new Map();
  for (const v of decorated) {
    const key = v.phase_number;
    if (!phaseMap.has(key)) phaseMap.set(key, []);
    phaseMap.get(key).push(v);
  }

  return Array.from(phaseMap.entries())
    .map(([phaseNum, rows]) => {
      const apptRows = rows.filter((r) => r.appointment_id);
      const date = apptRows.find((r) => r.appointment_starts_at)?.appointment_starts_at
        ?? apptRows.find((r) => r.scheduled_at)?.scheduled_at
        ?? null;
      const doctor = apptRows.find((r) => r.performed_by_name)?.performed_by_name ?? null;
      const allDone = rows.every((r) => r.completed_at);
      const anyDone = rows.some((r) => r.completed_at);
      const anyBooked = apptRows.length > 0;
      return { phaseNum, rows, date, doctor, allDone, anyDone, anyBooked };
    })
    .sort((a, b) => a.phaseNum - b.phaseNum);
}

// Short date for inside a sentence — "Jul 24", no year. The year is noise
// when the appointment is days away, which it almost always is.
function sentenceDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

// Strip the tooth/step decoration down to something that reads in a
// sentence. "Bridge - Zirconia Crown" stays as-is; we don't append the
// tooth number because the sentence is about timing, not clinical detail.
function procedureNameFor(phase) {
  const row = phase?.rows?.find((r) => !r.completed_at) || phase?.rows?.[0];
  if (!row) return null;
  const name = row.display_label || row.procedure_name || row.template_label;
  return name ? String(name).trim() : null;
}

function countWord(n) {
  if (n === 1) return "One";
  if (n === 2) return "Two";
  if (n === 3) return "Three";
  return String(n);
}

// ---------------------------------------------------------------------
// The money clause — "does he owe us money?"
// ---------------------------------------------------------------------
// Reads remaining_centavos from billing_state. Before acceptance there is
// deliberately no balance (the plan is a quote, not a debt), so a Draft or
// Proposed plan always reads "Nothing due yet" no matter what the quoted
// total says. That mirrors FinancialStatePanel's existing
// "Balance starts after acceptance" rule rather than inventing a second
// interpretation of the same state.
function moneyClause({ billingState, balance, plan, pipelineState, schedule, currency }) {
  const source = billingState || balance || null;
  const planStatus = String(plan?.status || source?.plan_status || "").toLowerCase();
  const preAcceptance = planStatus === "draft" || planStatus === "proposed";

  if (preAcceptance) {
    return { text: "Nothing due yet.", tone: "neutral" };
  }

  if (!source) {
    return { text: "No payment record yet.", tone: "neutral" };
  }

  const total = billingState
    ? Number(billingState.final_total_centavos ?? billingState.total_centavos ?? 0)
    : Number(balance?.total_centavos ?? 0);
  const paid = Number(source.paid_centavos ?? 0);
  const remaining = billingState
    ? Number(billingState.remaining_centavos ?? 0)
    : Number(balance?.balance_centavos ?? 0);

  if (remaining > 0) {
    const overdue = pipelineState === "ar"
      || (schedule?.rows || []).some((r) => r.status === "overdue");
    return {
      text: overdue
        ? `Owes ${formatMoney(remaining, currency)}, overdue.`
        : `Owes ${formatMoney(remaining, currency)}.`,
      tone: "owing",
    };
  }

  if (paid > 0) {
    return { text: "Paid in full.", tone: "settled" };
  }

  if (total <= 0) {
    return { text: "No fees on this plan.", tone: "neutral" };
  }

  return { text: "Nothing due yet.", tone: "neutral" };
}

// ---------------------------------------------------------------------
// The next clause — "when is he coming back?"
// ---------------------------------------------------------------------
function nextClause({ pipelineState, phases }) {
  const remaining = phases.filter((p) => !p.allDone);
  const nextBooked = remaining.find((p) => p.anyBooked && p.date) || null;

  switch (pipelineState) {
    case "draft":
      return "Not sent to the patient yet.";
    case "proposed":
      return "Waiting for the patient to accept.";
    case "expired":
      return "Proposal expired.";
    case "rejected":
      return "Patient declined this plan.";
    case "superseded":
      return "Replaced by a newer plan.";
    case "paused":
      return "Plan is on hold.";
    case "abandoned":
      return "Plan was abandoned.";
    case "completed":
    case "ar":
      return "All visits done.";
    default:
      break;
  }

  // Live states — needs_booking, scheduled, accepted, in_progress. The
  // sentence is about the next appointment, so booked-or-not is what
  // separates them, not the pipeline label.

  // No procedure_visits rows at all. Build Standard 14 says this shouldn't
  // happen on a live plan, so it means the data is wrong — say that
  // plainly instead of reporting a completion that never occurred.
  if (phases.length === 0) {
    return "No visits planned yet.";
  }

  if (remaining.length === 0) {
    return "All visits done.";
  }

  const left = `${countWord(remaining.length)} visit${remaining.length === 1 ? "" : "s"} left`;

  if (!nextBooked) {
    return `${left}, not booked yet.`;
  }

  const name = procedureNameFor(nextBooked);
  const when = sentenceDate(nextBooked.date);
  const tail = [name, when].filter(Boolean).join(", ");
  return tail ? `${left} — ${tail}.` : `${left}, booked.`;
}

export function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ---------------------------------------------------------------------
// Folder summary lines — what each closed folder still tells you.
// A shut folder that says nothing is just a hidden panel; these keep the
// number on screen so front desk only opens a folder when they want the
// breakdown, not to find out whether it matters.
// ---------------------------------------------------------------------
export function folderSummaries({ data, currency = "PHP" }) {
  if (!data) return { treatment: null, payments: null, history: null };

  const items = (data.plan?.items || [])
    .filter((it) => !["Cancelled", "Declined"].includes(it.status));
  const phases = groupVisitPhases({ visits: data.visits, items: data.plan?.items });
  const donePhases = phases.filter((p) => p.allDone).length;

  const treatmentParts = [`${items.length} item${items.length === 1 ? "" : "s"}`];
  if (phases.length > 0) {
    treatmentParts.push(`${donePhases} of ${phases.length} visit${phases.length === 1 ? "" : "s"} done`);
  }

  const billing = data.billing_state;
  const legacy = data.balance;
  const planStatus = String(data.plan?.status || billing?.plan_status || "").toLowerCase();
  const preAcceptance = planStatus === "draft" || planStatus === "proposed";
  const total = billing
    ? Number(billing.final_total_centavos ?? billing.total_centavos ?? 0)
    : Number(legacy?.total_centavos ?? 0);
  const paid = Number((billing || legacy)?.paid_centavos ?? 0);

  const payments = preAcceptance
    ? `Quoted ${formatMoney(total, currency)}`
    : `${formatMoney(paid, currency)} of ${formatMoney(total, currency)}`;

  const latest = (data.activity || [])[0] || null;

  return {
    treatment: treatmentParts.join(" · "),
    payments,
    history: latest
      ? `Last: ${latest.description}, ${formatRelative(latest.occurred_at)}`
      : "No activity yet",
  };
}

// ---------------------------------------------------------------------
// Public entry point. Returns the two clauses plus the tone that colors
// the money half. Pure — no fetching, no state.
// ---------------------------------------------------------------------
export function planAnswer({ data, schedule, currency = "PHP" }) {
  if (!data) return null;
  const pipelineState = data.pipeline_state || null;
  const phases = groupVisitPhases({
    visits: data.visits,
    items: data.plan?.items,
  });
  const money = moneyClause({
    billingState: data.billing_state,
    balance: data.balance,
    plan: data.plan,
    pipelineState,
    schedule,
    currency,
  });
  return {
    money: money.text,
    tone: money.tone,
    next: nextClause({ pipelineState, phases }),
  };
}
